"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { Search, X } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { publicService } from "@/modules/public/home/services/publicService";
import { Product } from "@/modules/public/home/types/public.types";

export default function SearchBox() {
    const router = useRouter();
    const searchParams = useSearchParams();

    const urlQuery = searchParams.get("q") || "";
    const [query, setQuery] = useState(urlQuery);
    const [prevUrlQuery, setPrevUrlQuery] = useState(urlQuery);
    const [suggestions, setSuggestions] = useState<Product[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);

    const searchRef = useRef<HTMLDivElement>(null);

    // Sync state with URL search parameters if any change (e.g. from the search page itself)
    if (urlQuery !== prevUrlQuery) {
        setQuery(urlQuery);
        setPrevUrlQuery(urlQuery);
        if (urlQuery.trim().length < 2) {
            setSuggestions([]);
        }
    }

    // Handle clicking outside to close suggestions dropdown
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Live suggestion fetcher with 300ms debounce
    useEffect(() => {
        if (query.trim().length < 2) {
            return;
        }

        const delayDebounceFn = setTimeout(async () => {
            setLoadingSuggestions(true);
            try {
                const res = await publicService.getProducts({
                    search: query.trim(),
                    limit: 8,
                });
                setSuggestions(res.products || []);
            } catch (err) {
                console.error("Error fetching suggestions:", err);
            } finally {
                setLoadingSuggestions(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [query]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setShowDropdown(false);
        const params = new URLSearchParams();
        if (query.trim()) {
            params.set("q", query.trim());
        }
        router.push(`/search?${params.toString()}`);
    };

    const handleProductClick = (prod: Product) => {
        setShowDropdown(false);
        const currentCategorySlug = prod.category?.slug || "general";
        const currentSubcategorySlug = prod.subcategory?.slug || "uncategorized";
        router.push(`/${currentCategorySlug}/${currentSubcategorySlug}/${prod.slug}`);
    };

    return (
        <div ref={searchRef} className="w-full relative">
            <form
                onSubmit={handleSearch}
                className="w-full flex items-center bg-white text-gray-800 rounded-sm border border-slate-300 shadow-xs focus-within:border-slate-400 focus-within:shadow-sm transition-all duration-200"
            >
                {/* Input Field */}
                <div className="flex-1 relative min-w-0 flex items-center h-10">
                    <input
                        type="text"
                        placeholder="Search products, brands, or categories..."
                        value={query}
                        onChange={(e) => {
                            const val = e.target.value;
                            setQuery(val);
                            if (val.trim().length < 2) {
                                setSuggestions([]);
                            }
                            setShowDropdown(true);
                        }}
                        onFocus={() => setShowDropdown(true)}
                        className="w-full h-full bg-transparent text-slate-800 pl-4 pr-10 focus:outline-none text-xs sm:text-sm font-medium placeholder:text-gray-400"
                    />
                    {query && (
                        <button
                            type="button"
                            onClick={() => {
                                setQuery("");
                                setSuggestions([]);
                            }}
                            className="absolute right-3 text-slate-400 hover:text-slate-600 transition cursor-pointer"
                            aria-label="Clear search"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {/* Search Button */}
                <button
                    type="submit"
                    className="w-12 h-10 border-l border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-center transition-all duration-200 cursor-pointer shrink-0"
                    aria-label="Search"
                >
                    <Search className="w-4.5 h-4.5" />
                </button>
            </form>

            {/* suggestions dropdown */}
            {showDropdown && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-lg border border-slate-200 shadow-xl overflow-hidden z-[100] animate-in fade-in slide-in-from-top-2 duration-200 max-h-[420px] overflow-y-auto">
                    <div className="py-2 divide-y divide-slate-100">
                        {suggestions.map((prod) => {
                            const activeVariant = prod.variants && prod.variants.length > 0
                                ? prod.variants.reduce((min, v) => v.salePrice < min.salePrice ? v : min, prod.variants[0])
                                : null;
                            const salePrice = activeVariant ? activeVariant.salePrice : null;
                            
                            const subcatName = typeof prod.subcategory === "object" && prod.subcategory !== null
                                ? prod.subcategory.name
                                : (typeof prod.subcategory === "string" ? prod.subcategory : "");

                            return (
                                <div
                                    key={prod._id}
                                    onClick={() => handleProductClick(prod)}
                                    className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50 cursor-pointer transition duration-150"
                                >
                                    {/* Thumbnail */}
                                    <div className="w-12 h-12 relative bg-white border border-slate-100 rounded-lg overflow-hidden shrink-0 flex items-center justify-center p-1.5">
                                        {prod.imageUrl ? (
                                            <Image
                                                src={prod.imageUrl}
                                                alt={prod.title}
                                                fill
                                                sizes="48px"
                                                className="object-contain p-0.5"
                                            />
                                        ) : (
                                            <Search className="w-5 h-5 text-slate-300" />
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="flex-grow min-w-0">
                                        <h4 className="font-bold text-slate-800 text-sm truncate">
                                            {prod.title}
                                        </h4>
                                        {subcatName && (
                                            <p className="text-[10px] font-black text-emerald-600 uppercase tracking-wider mt-0.5 animate-pulse">
                                                {subcatName}
                                            </p>
                                        )}
                                    </div>

                                    {/* Price */}
                                    <div className="shrink-0 text-right">
                                        {salePrice !== null ? (
                                            <span className="font-extrabold text-slate-800 text-sm">
                                                ₹{salePrice}
                                            </span>
                                        ) : (
                                            <span className="text-xs font-bold text-slate-400 italic">
                                                Quote
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Bottom Link */}
                    <div className="bg-slate-50 border-t border-slate-100 px-4 py-3 flex justify-start">
                        <button
                            type="button"
                            onClick={(e) => {
                                handleSearch(e);
                            }}
                            className="text-xs text-emerald-700 hover:text-emerald-800 font-extrabold hover:underline cursor-pointer flex items-center gap-1"
                        >
                            See all results for &quot;{query}&quot; →
                        </button>
                    </div>
                </div>
            )}

            {/* No matches dropdown */}
            {showDropdown && query.trim().length >= 2 && suggestions.length === 0 && !loadingSuggestions && (
                <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-lg border border-slate-200 shadow-xl p-5 text-center z-[100] text-slate-500 text-sm">
                    No products matching &quot;{query}&quot; found.
                </div>
            )}
        </div>
    );
}



